import Component from '@glimmer/component'
import type { WithBoundArgs } from '@glint/template'
import { hash } from '@ember/helper'
import { pick } from 'lodash'
import type { DurationOptions } from 'teacher-ui-client/components/stable-table/basic-cells/duration-unit'
import type { RowId, ColumnObject, RowObject } from 'teacher-ui-client/components/stable-table/basic-table/cell'
import { BasicTableHead } from 'teacher-ui-client/components/stable-table/basic-table/head'
import { BasicTableBody } from 'teacher-ui-client/components/stable-table/basic-table/body'
import { BasicTableFoot } from 'teacher-ui-client/components/stable-table/basic-table/foot'
import { BasicTableSpanner } from 'teacher-ui-client/components/stable-table/basic-table/spanner'

export interface Config {
  onSelectClick?: (selectedIds: RowId[]) => any
  onSortClick?: (sortObject: SortObject) => unknown
  allSelectableIds?: RowId[]
  selectedIds?: RowId[]
  sortingConfig?: SortingConfig
  multipleSortIcons?: boolean
}

export interface DurationConfig {
  dataInterval: string
  splitDurationOptions: DurationOptions
}

interface SortingConfig {
  columns: SortColumn[]
}

export interface SortColumn {
  key: string
  comparerKey?: SortComparerKey
  sortDirection: SortDirection
}

type SortDirection = 'asc' | 'desc'
type SortComparerKey = 'sortByNumber' | 'sortByDate' | 'sortByValue' | 'sortByGrade'

interface BasicTableSignature {
  Element: HTMLTableElement
  Args: BasicTableArgs
  Blocks: {
    default: [
      {
        head: WithBoundArgs<typeof BasicTableHead, 'columns' | 'config'>
        body: WithBoundArgs<typeof BasicTableBody, 'columns' | 'config' | 'rows'>
        foot: WithBoundArgs<typeof BasicTableFoot, 'columns' | 'config' | 'rows'>
        spanner: WithBoundArgs<typeof BasicTableSpanner, 'columns'>
      },
      BasicTableArgs['rows'],
      BasicTableArgs['columns'],
      BasicTableArgs['footerRows'],
      BasicTableArgs['config'],
    ]
  }
}

interface BasicTableArgs {
  rows: RowObject[]
  columns: ColumnObject[]
  exportable?: boolean
  footerRows?: RowObject[]
  config?: Config
}

export class BasicTable extends Component<BasicTableSignature> {
  /**
   * Make a copy of the columns for use by the footer. Remove any cell modifications so that they aren't applied to the
   * footer cells.
   */
  get footerColumns() {
    return this.args.columns.map((col) => {
      const keysToRemove = ['valuePath', 'cellClassNames', 'cellComponent', 'cellComponentClassNames']
      const keys = Object.keys(col).filter((key) => !keysToRemove.includes(key))
      return pick(col, keys)
    })
  }

  <template>
    <table data-test-basic-table ...attributes>
      {{yield
        (hash
          head=(component BasicTableHead columns=@columns config=@config)
          body=(component BasicTableBody rows=@rows columns=@columns config=@config)
          foot=(component BasicTableFoot rows=@footerRows columns=this.footerColumns config=@config)
          spanner=(component BasicTableSpanner columns=@columns)
        )
        @rows
        @columns
        @footerRows
        @config
      }}
    </table>
  </template>
}

export default BasicTable
